import {ReactElement, useEffect, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router";
import {useDispatch, useSelector} from "react-redux";
import {
    ReferralLinkTextBody,
    ReferralsApi,
    UsersApi,
    ErrorType,
    OWUserSessionEventType,
} from "@devour/client";
import {IStore} from "../redux/defaultStore";
import AuthHeaderLogo from "../components/auth/AuthHeaderLogo";
import {
    addError,
    decrementLoading,
    toggleLoginOpen,
} from "../redux/meta/metaActions";
import getConfig from "../utils/getConfig";
import {AiFillCheckCircle} from "react-icons/ai";
import classNames from "classnames";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";
import LoginEmail from "@/components/login/LoginEmail";
import LoginRegistration from "@/components/login/LoginRegistration";
import LoginVerificationCode from "@/components/login/LoginVerificationCode";
import SocialLoginButtons, { SocialProvider } from "@/components/login/SocialLoginButtons";
import EmailCollection from "@/components/login/EmailCollection";
import {emitLoginEvent, emitRegisterEvent} from "@/hooks/useOnLogin";
import {
    inAppWallet,
    preAuthenticate,
    getUserEmail,
    linkProfile,
    getProfiles,
    unlinkProfile,
} from "thirdweb/wallets/in-app";
import {Wallet} from "thirdweb/wallets";
import useOnAuthThirdweb from "@/hooks/useOnAuthThirdweb";
import {useConnect} from "thirdweb/react";
import {thirdwebAuthClient} from "@/components/auth/ThirdwebClient";
import {logOverwolfUserEvent} from "@/utils/logOverwolfUserEvent";
import { useGetGuild } from "@/hooks/useGetGuild";
import { useAsset } from "@/hooks/useAsset";
import { useGetChallenge } from "@/hooks/challenges/useGetChallenge";

enum LoginState {
    EMAIL,
    REGISTRATION,
    VERIFY_CODE,
    SOCIAL_SELECTION,
    EMAIL_COLLECTION,
    LINK_EMAIL_VERIFY_CODE,
}

interface Props {
    isOnDialog?: boolean;
    onInvitedRegistered?: () => void
}

function ThirdwebLogin(props: Props): ReactElement {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const referralCodeQuery = searchParams.get("ref");
    const [loginState, setLoginState] = useState<LoginState>(LoginState.EMAIL);
    const [referralLinkText, setReferralLinkText] = useState<ReferralLinkTextBody | undefined>(undefined);
    const [email, setEmail] = useState<string | null>(null);
    const [hasVerificationCodeError, setHasVerificationCodeError] = useState<boolean>(false);
    const [selectedSocialProvider, setSelectedSocialProvider] = useState<SocialProvider | null>(null);
    const [socialWallet, setSocialWallet] = useState<Wallet<"inApp"> | null>(null);
    const reduxReferralCode = useSelector((store: IStore) => store.affiliateStore.referralCode);
    const reduxReferralExpiry = useSelector((store: IStore) => store.affiliateStore.referralExpiry);
    const reduxReferralValid = reduxReferralCode && Date.now() < reduxReferralExpiry ? reduxReferralCode : "";
    const [referralCode, setReferralCode] = useState<string>(reduxReferralValid ?? referralCodeQuery ?? "");
    const {isOnOverwolf} = useOverwolfInterop();
    const {connect} = useConnect();
    const {onAuthComplete} = useOnAuthThirdweb();
    const navigate = useNavigate();
    const isSubmittingRef = useRef(false);
    const guildInviteId = searchParams.get("guildId");
    const challengeInviteID = searchParams.get("challengeId");
    const {data: guildInfo} = useGetGuild(guildInviteId);
    const {data: challengeInfo} = useGetChallenge(challengeInviteID);
    const isInvite = !!referralCode;
    const isGuildInvite = isInvite && guildInviteId;
    const isChallengeInvite = isInvite && challengeInviteID;

    useEffect(() => {
        /**
         * Get the current referral link text.
         *
         */
        async function getReferralLinkText() {
            try {
                const formData = await new ReferralsApi(getConfig()).getReferralLinkText({
                    referralCode: reduxReferralCode,
                });

                setReferralLinkText({
                    header: formData.header,
                    description: formData.description,
                    relationshipType: formData.relationshipType,
                });
            } catch (e) {
                dispatch(await addError(e));
            } finally {
                dispatch(decrementLoading());
            }
        }

        // only render if there was a referral code stored
        if (reduxReferralCode) {
            void getReferralLinkText();
        }
    }, [reduxReferralCode]);


    async function onSubmitEmail(email: string): Promise<void> {
        if (isSubmittingRef.current) {
            return;
        }
        try {
            isSubmittingRef.current = true;
            const res = await new UsersApi(getConfig()).validateUserExistence({
                email,
            });
            if (res.disabled) {
                throw new Error("Your account has been disabled.");
            }
            setEmail(email);
            if (res._exists) {
                // send email verification code
                await preAuthenticate({
                    client: thirdwebAuthClient,
                    strategy: "email",
                    email, // ex: user@example.com
                });
                setLoginState(LoginState.VERIFY_CODE);
            } else {
                setLoginState(LoginState.REGISTRATION);
            }
        } catch (e) {
            let errorMessage = e.message || "Unable to process registration";
            if (e?.message === "Failed to send verification code") {
                errorMessage = "Unable to send verification code. You may have requested a code too recently. Please wait one minute and try again.";
            }
            dispatch(await addError({
                type: ErrorType.APP,
                message: errorMessage,
            }));
        } finally {
            isSubmittingRef.current = false;
        }
    }

    async function onSubmitRegistration(referralCode?: string): Promise<void> {
        if (isSubmittingRef.current) {
            return;
        }
        try {
            isSubmittingRef.current = true;

            // If we have a social wallet, complete registration with it
            if (socialWallet) {
                if (referralCode) {
                    setReferralCode(referralCode);
                }
                void onNewLoginComplete(socialWallet);
                return;
            }

            // Otherwise, do email verification
            await preAuthenticate({
                client: thirdwebAuthClient,
                strategy: "email",
                email, // ex: user@example.com
            });
            if (referralCode) {
                setReferralCode(referralCode);
            }
            setLoginState(LoginState.VERIFY_CODE);
        } catch (e) {
            dispatch(await addError({
                type: ErrorType.APP,
                message: e.message || "Unable to submit registration code",
            }));
        } finally {
            isSubmittingRef.current = false;
        }
    }

    async function onSubmitVerificationCode(code: string): Promise<void> {
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;
        await connect(async () => {
            try {
                const wallet = inAppWallet();
                await wallet.connect({
                    client: thirdwebAuthClient,
                    strategy: "email",
                    email,
                    verificationCode: code,
                });
                void onNewLoginComplete(wallet);
                return wallet;
            } catch (e) {
                if (e?.message === "Failed to verify verification code") {
                    setHasVerificationCodeError(true);
                } else {
                    dispatch(await addError({
                        type: ErrorType.APP,
                        message: e.message || "Unable to verify registration code",
                    }));
                }
            } finally {
                isSubmittingRef.current = false;
            }
        });
    }

    async function onRequestNewCode(): Promise<void> {
        setHasVerificationCodeError(false);
        await onSubmitRegistration();
    }

    async function onSocialProviderSelect(provider: SocialProvider): Promise<void> {
        if (isSubmittingRef.current) return;

        try {
            isSubmittingRef.current = true;
            setSelectedSocialProvider(provider);

            // Handle email-based login separately
            if (provider === SocialProvider.EMAIL) {
                setLoginState(LoginState.EMAIL);
                setSelectedSocialProvider(null);
                isSubmittingRef.current = false;
                return;
            }


            // Connect with social provider using connect hook
            const wallet = inAppWallet();

            let connectedSuccessfully = false;

            // Use the connect hook properly
            await connect(async () => {
                await wallet.connect({
                    client: thirdwebAuthClient,
                    strategy: provider,
                });

                connectedSuccessfully = true;
                return wallet;
            });

            // Only proceed if connection was successful
            if (!connectedSuccessfully) {
                // User cancelled or connection failed
                setSelectedSocialProvider(null);
                return;
            }

            // After successful connection, check for email
            try {
                setSocialWallet(wallet);

                // Try to get the email from the provider
                let providerEmail = null;
                try {
                    // Use getUserEmail to get email from the authenticated wallet
                    providerEmail = await getUserEmail({ client: thirdwebAuthClient });
                    console.log("Got email from provider:", providerEmail);

                    if (providerEmail) {
                        // Provider gave us email, proceed with normal flow
                        setEmail(providerEmail);

                        // Check if user exists
                        const res = await new UsersApi(getConfig()).validateUserExistence({
                            email: providerEmail,
                        });

                        if (res.disabled) {
                            throw new Error("Your account has been disabled.");
                        }

                        if (res._exists) {
                            // Existing user, complete login
                            await onNewLoginComplete(wallet);
                        } else {
                            // New user, show registration
                            setLoginState(LoginState.REGISTRATION);
                        }
                    } else {
                        // No email from provider, need to collect it
                        console.log("No email from provider, collecting manually");
                        setLoginState(LoginState.EMAIL_COLLECTION);
                    }
                } catch (emailError) {
                    console.log("Could not get email from provider:", emailError);
                    // If we can't get email, collect it manually
                    setLoginState(LoginState.EMAIL_COLLECTION);
                }
            } catch (postConnectError) {
                console.error("Post-connection error:", postConnectError);
                throw postConnectError;
            }
        } catch (e) {
            console.error("Social login error:", e);
            // Check if user cancelled the auth (common error messages)
            const errorMessage = e?.message?.toLowerCase() || "";
            if (errorMessage.includes("user rejected") ||
                errorMessage.includes("user denied") ||
                errorMessage.includes("user closed") ||
                errorMessage.includes("cancelled")) {
                // User cancelled - no error message needed
                setSelectedSocialProvider(null);
            } else {
                // Real error - show message
                dispatch(await addError({
                    type: ErrorType.APP,
                    message: e.message || `Unable to login with ${provider}`,
                }));
                setSelectedSocialProvider(null);
            }
        } finally {
            isSubmittingRef.current = false;
        }
    }

    async function onEmailCollected(collectedEmail: string): Promise<void> {
        /*
         * This is called after phone collection when phone doesn't have a profile
         * Two scenarios:
         * 1. Email exists in DB - auto-trigger email verify, link phone, verify phone
         * 2. Email not in DB - verify email, link phone, verify phone
         */

        try {
            setEmail(collectedEmail);

            // Check if user exists
            const res = await new UsersApi(getConfig()).validateUserExistence({
                email: collectedEmail,
            });

            if (res.disabled) {
                throw new Error("Your account has been disabled.");
            }

            if (res._exists) {
                /*
                 * Scenario 1: Email EXISTS in DB
                 * Auto-trigger email verification
                 */
                await preAuthenticate({
                    client: thirdwebAuthClient,
                    strategy: "email",
                    email: collectedEmail,
                });

                // Show screen to enter email verification code
                setLoginState(LoginState.LINK_EMAIL_VERIFY_CODE);
            } else {
                /*
                 * Scenario 2: Email NOT in DB - new user
                 * Show registration to collect name
                 */
                setLoginState(LoginState.REGISTRATION);
            }
        } catch (e) {
            dispatch(await addError({
                type: ErrorType.APP,
                message: e.message || "Unable to process email",
            }));
        }
    }

    async function onLinkEmailVerificationCode(code: string): Promise<void> {
        if (!socialWallet || !email || isSubmittingRef.current) return;

        try {
            isSubmittingRef.current = true;

            /*
             * Scenario 1: Email EXISTS in DB
             * Link the email profile to the phone wallet
             */
            try {
                await linkProfile({
                    client: thirdwebAuthClient,
                    strategy: "email",
                    email: email,
                    verificationCode: code,
                });

                // Successfully linked - now complete login
                await onNewLoginComplete(socialWallet, email);
            } catch (linkError) {
                // If linking fails, unlink the phone profile and show error
                try {
                    const profiles = await getProfiles({ client: thirdwebAuthClient });
                    const phoneProfile = profiles.find(p => p.type === "phone");
                    if (phoneProfile) {
                        await unlinkProfile({
                            client: thirdwebAuthClient,
                            type: "phone",
                        });
                    }
                } catch (unlinkError) {
                    // Ignore unlink errors
                }
                throw linkError;
            }
        } catch (e) {
            dispatch(await addError({
                type: ErrorType.APP,
                message: e.message || "Unable to link email to your account",
            }));
        } finally {
            isSubmittingRef.current = false;
        }
    }

    async function onNewLoginComplete(wallet: Wallet<"inApp">, collectedEmail?: string) {
        if (!wallet) {
            return;
        }
        const loginRes = await onAuthComplete(wallet, referralCode, collectedEmail);

        dispatch(toggleLoginOpen(false));
        if (isInvite && !loginRes.isExistingUser && props.onInvitedRegistered) {
            props.onInvitedRegistered();
        }

        if (loginRes) {

            if (loginRes.isExistingUser) {
                emitLoginEvent();
                if (isOnOverwolf) {
                    void logOverwolfUserEvent(OWUserSessionEventType.LOGIN);
                }
            } else {
                emitRegisterEvent();
                if (isOnOverwolf) {
                    void logOverwolfUserEvent(OWUserSessionEventType.SIGNUP);
                }
            }
            if (!props.isOnDialog) {
                navigate("/");
            }
        }
    }

    function getInviteDescription() {
        if (isChallengeInvite) {
            return `Your friend invite you to join them in the ${challengeInfo?.name}. Enter your email address to create an account and then join the challenge to complete!`;
        }
        if (isGuildInvite) {
            return `Your friend invited you to join ${guildInfo?.name} guild in DevourPlay. Please sign up to get started!`;
        }
        if (isInvite) {
            return "You were invited by a friend to join Devour, where gamers earn rewards for playing games! Enter your email below to get started!";
        }
    }


    function renderIntro() {
        if (loginState === LoginState.VERIFY_CODE) {
            return null;
        }

        if (loginState === LoginState.REGISTRATION) {
            return (
                <>
                    <h3 className="login-page_content_title">Create an Account!</h3>
                    <p className="login-page_content_subtitle">
                        Confirm that you are 18 or above, accept terms and then press Create Account to continue.
                    </p>
                </>
            );
        }

        if (isInvite) {
            return (
                <>
                    <h3 className="login-page_content_title">You Are Invited!</h3>
                    <p className="login-page_content_subtitle">
                        {getInviteDescription()}
                    </p>
                </>
            );
        }
        if (isGuildInvite) {
            return (
                <>
                    <h3 className="login-page_content_title">You Are Invited!</h3>
                    <p className="login-page_content_subtitle">
                        Your friend invited you to join {guildInfo?.name} guild in DevourPlay. Please sign up to get started!
                    </p>
                </>
            );
        }
        if (isInvite) {
            return (
                <>
                    <h3 className="login-page_content_title">You Are Invited!</h3>
                    <p className="login-page_content_subtitle">
                        You were invited by a friend to join Devour, where gamers earn rewards for playing games! Enter your email below to get started!
                    </p>
                </>
            );
        }
        return (
            <>
                {!isOnOverwolf &&
                    <>
                        <h3 className="login-page_content_title">Play. Earn. Repeat.</h3>
                        <p className="login-page_content_subtitle">
                            Earn XP, Fuel, and drops—just for playing.
                        </p>
                    </>
                }

                {reduxReferralValid && referralLinkText && !isGuildInvite &&
                    <div className="sign-up_content_referral-link-container">
                        <div className="sign-up_content_referral-link-container_header">
                            <AiFillCheckCircle
                                className="sign-up_content_referral-link-container_header_icon"/>
                            <p className="sign-up_content_referral-link-container_text">{referralLinkText.header}</p>
                        </div>
                        {referralLinkText.description && referralLinkText.description !== "" &&
                            <p className="sign-up_content_referral-link-container_text">
                                {referralLinkText.description}
                            </p>
                        }
                    </div>
                }

                <h4 className="login-page_content_login-text">
                    Login or Sign Up
                </h4>
                {isOnOverwolf &&
                    <p className="login-page_content_login-description">
                        DevourPlay delivers more than just food — serving up a new way to eat, play,
                        and earn!
                    </p>
                }
            </>
        );
    }

    function renderForm() {
        switch (loginState) {
            case LoginState.REGISTRATION:
                return (
                    <LoginRegistration
                        initialReferralCode={referralCode}
                        email={email}
                        onSubmit={onSubmitRegistration}
                        disableReferralField={!!(referralCodeQuery && reduxReferralValid)}
                    />
                );
            case LoginState.VERIFY_CODE:
                return (
                    <LoginVerificationCode
                        email={email}
                        hasError={hasVerificationCodeError}
                        onRequestNewCode={onRequestNewCode}
                        onSubmit={onSubmitVerificationCode}
                    />
                );
            case LoginState.EMAIL_COLLECTION:
                return (
                    <EmailCollection
                        provider={selectedSocialProvider}
                        onSubmit={onEmailCollected}
                        onBack={() => {
                            // Go back to appropriate state based on provider
                            if (selectedSocialProvider === SocialProvider.PHONE) {
                                setLoginState(LoginState.SOCIAL_SELECTION);
                            } else {
                                setLoginState(LoginState.SOCIAL_SELECTION);
                            }
                            setSocialWallet(null);
                            setSelectedSocialProvider(null);
                        }}
                        isLoading={isSubmittingRef.current}
                    />
                );
            case LoginState.LINK_EMAIL_VERIFY_CODE:
                return (
                    <LoginVerificationCode
                        email={email}
                        hasError={hasVerificationCodeError}
                        onRequestNewCode={() => onEmailCollected(email!)}
                        onSubmit={onLinkEmailVerificationCode}
                    />
                );
            case LoginState.EMAIL:
            default:
                return (
                    <>
                        <SocialLoginButtons
                            onProviderSelect={onSocialProviderSelect}
                            isLoading={isSubmittingRef.current}
                            selectedProvider={selectedSocialProvider}
                        />
                        <LoginEmail
                            initialEmail={email}
                            onSubmit={onSubmitEmail}
                        />
                    </>
                );
        }
    }

    return (
        <div
            className={classNames("login-page", {
                "is-on-dialog": !!props.isOnDialog,
            })}
        >
            {!props.isOnDialog &&
                <div className="login-page_spacer-top">
                    <AuthHeaderLogo/>
                </div>
            }

            <div className="login-page_content">

                {renderIntro()}
                {renderForm()}
            </div>

            <div className="login-page_spacer-bottom"/>
        </div>
    );
}

export default ThirdwebLogin;